import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaPhoneAlt, FaEnvelope, FaClock, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Base from './Base';
import { sendAppointmentEmail } from '../Utils/EmailService';

function Appointment() {
   const [formData, setFormData] = useState({
      name: '',
      email: '',
      phone: '',
      department: '',
      message: ''
   });
   const [isSubmitting, setIsSubmitting] = useState(false);

   const handleChange = (e) => {
      setFormData({
         ...formData,
         [e.target.name]: e.target.value
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Validation
      if (!formData.name || !formData.email || !formData.phone || !formData.department) {
         toast.error('Please fill all required fields');
         return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
         toast.error('Please enter a valid email address');
         return;
      }

      setIsSubmitting(true);

      try {
         const success = await sendAppointmentEmail(formData);
         
         if (success) {
            toast.success('Appointment request submitted successfully! We will contact you soon via email.');
         } else {
            toast.info('Please send the email from your email client. We will contact you soon!');
         }
         
         // Reset form
         setFormData({
            name: '',
            email: '',
            phone: '',
            department: '',
            message: ''
         });
      } catch (error) {
         console.error('Appointment submission error:', error);
         toast.error('Failed to submit appointment. Please try again or contact us directly at +92-345 5558297');
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Base>
         <div className="dark:bg-dark-800 min-h-screen transition-colors duration-200">
            <Helmet>
              <title>Book Appointment | Aman Medical Complex Gilgit</title>
              <meta name="description" content="Book your appointment online at Aman Medical Complex Gilgit. Easy appointment booking for Cardiology, Neurology, Pediatrics, Orthopedics, and more." />
              <meta name="keywords" content="book appointment, online appointment, hospital appointment gilgit, medical appointment pakistan" />
              <meta property="og:title" content="Book Appointment | Aman Medical Complex Gilgit" />
              <meta property="og:description" content="Schedule your medical appointment online. Our team will contact you within 24 hours." />
            </Helmet>

            {/* Page Title Section */}
            <section className="relative py-24 overflow-hidden bg-gradient-to-br from-primary-50 to-white dark:from-dark-800 dark:to-dark-900">
               <div className="absolute inset-0 z-0 opacity-5">
                  <div className="absolute top-10 left-10 w-72 h-72 bg-primary-500 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-10 right-10 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
               </div>
               <div className="container mx-auto px-4 text-center relative z-10">
                  <span className="text-primary-500 font-bold uppercase tracking-[0.2em] text-xs md:text-sm inline-block mb-4">BOOK APPOINTMENT</span>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-navy-500 dark:text-white mb-6 tracking-tight">Make an Appointment</h1>
                  <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 font-semibold text-sm">
                     <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
                     <span>/</span>
                     <span className="text-primary-500">Appointment</span>
                  </div>
               </div>
            </section>

            <section className="py-24 container mx-auto px-4 max-w-6xl">
               <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-strong overflow-hidden flex flex-col lg:flex-row border border-gray-100 dark:border-dark-700">
                  <div className="lg:w-1/3 bg-gradient-primary p-12 text-white">
                     <h2 className="text-3xl font-black mb-8">Contact Information</h2>
                     <p className="opacity-90 mb-10 leading-relaxed">Fill out the form and our team will get back to you within 24 hours.</p>

                     <div className="space-y-6">
                        <div className="flex items-center gap-4">
                           <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-xl">
                              <FaPhoneAlt />
                           </div>
                           <div>
                              <p className="text-sm opacity-70 font-semibold">Call Us</p>
                              <p className="font-bold text-lg">+92-345 5558297</p>
                           </div>
                        </div>
                        <div className="flex items-center gap-4">
                           <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-xl">
                              <FaEnvelope />
                           </div>
                           <div>
                              <p className="text-sm opacity-70 font-semibold">Email Us</p>
                              <p className="font-bold text-lg">ehtishamglt@gmail.com</p>
                           </div>
                        </div>
                        <div className="flex items-center gap-4">
                           <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-xl">
                              <FaClock />
                           </div>
                           <div>
                              <p className="text-sm opacity-70 font-semibold">Working Hours</p>
                              <p className="font-bold text-lg">24/7 Service Available</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="lg:w-2/3 p-12">
                     <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                           <label htmlFor="appointment-name" className="block text-gray-700 dark:text-gray-200 font-bold mb-2 text-sm">Your Name <span className="text-red-500" aria-label="required">*</span></label>
                           <input 
                              id="appointment-name"
                              type="text" 
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="Full Name" 
                              required
                              aria-required="true"
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-400 outline-none transition-all" 
                           />
                        </div>
                        <div>
                           <label htmlFor="appointment-email" className="block text-gray-700 dark:text-gray-200 font-bold mb-2 text-sm">Email Address <span className="text-red-500" aria-label="required">*</span></label>
                           <input 
                              id="appointment-email"
                              type="email" 
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="email@example.com" 
                              required
                              aria-required="true"
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-400 outline-none transition-all" 
                           />
                        </div>
                        <div>
                           <label htmlFor="appointment-phone" className="block text-gray-700 dark:text-gray-200 font-bold mb-2 text-sm">Phone Number <span className="text-red-500" aria-label="required">*</span></label>
                           <input 
                              id="appointment-phone"
                              type="tel" 
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+92-345 5558297" 
                              required
                              aria-required="true"
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-400 outline-none transition-all" 
                           />
                        </div>
                        <div>
                           <label htmlFor="appointment-department" className="block text-gray-700 dark:text-gray-200 font-bold mb-2 text-sm">Department <span className="text-red-500" aria-label="required">*</span></label>
                           <select 
                              id="appointment-department"
                              name="department"
                              value={formData.department}
                              onChange={handleChange}
                              required
                              aria-required="true"
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-400 outline-none transition-all"
                           >
                              <option value="">Select Department</option>
                              <option value="Cardiology">Cardiology</option>
                              <option value="Neurology">Neurology</option>
                              <option value="Pediatrics">Pediatrics</option>
                              <option value="Orthopedics">Orthopedics</option>
                              <option value="Emergency Care">Emergency Care</option>
                              <option value="General Medicine">General Medicine</option>
                           </select>
                        </div>
                        <div className="md:col-span-2">
                           <label htmlFor="appointment-message" className="block text-gray-700 dark:text-gray-200 font-bold mb-2 text-sm">Your Message</label>
                           <textarea 
                              id="appointment-message"
                              rows="4" 
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              placeholder="How can we help you?" 
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-700 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-400 outline-none transition-all"
                           ></textarea>
                        </div>
                        <div className="md:col-span-2">
                           <button 
                              type="submit"
                              disabled={isSubmitting}
                              aria-label={isSubmitting ? "Submitting appointment request" : "Submit appointment form"}
                              className={`group bg-gradient-primary text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-card-hover w-full flex items-center justify-center gap-3 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                           >
                              {isSubmitting ? 'Submitting...' : 'Confirm Appointment'} 
                              {!isSubmitting && <FaArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />}
                           </button>
                        </div>
                     </form>
                  </div>
               </div>
            </section>
         </div>
      </Base>
   );
}

export default Appointment;
